import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Constants } from "config/constants";
import { Formik, Field } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { addComment, editComment } from "state/slices/comment";
import _get from "lodash/get";
import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import format from "date-fns/format";

const FinancialStatements = ({ isViewMode }) => {
  const dispatch = useDispatch();
  const { comments } = useSelector((state) => state.commentsSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions } = transactionUnderReview.items;
  const yyyyFinancialStatementsTransaction = transactions.find(
    (trx) =>
      trx.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
  );
  const yyyyFinancialStatementsIndex = transactions.findIndex(
    (trx) => trx._id === yyyyFinancialStatementsTransaction?._id
  );
  const yyyyFinancialStatementsUpdateEffectPath = buildUpdateEffectPath(
    yyyyFinancialStatementsIndex
  );
  const yyyyFinancialStatementsComments = comments.filter(
    (comment) =>
      comment.transaction === yyyyFinancialStatementsTransaction?._id &&
      comment.formName === "financialStatements"
  );
  const updateEffectKey = "updateTransactionUnderReview";
  const formHelperKey = "initiateTransactionChange";
  const financialStatementsArray =
    yyyyFinancialStatementsTransaction.financialStatements;
  const documentData = generateDocumentDataFromFinancialStatementsArray(
    financialStatementsArray
  );
  const fileUploads = generateFileUploadsFromFiancialStatementsArray(
    financialStatementsArray
  );

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: yyyyFinancialStatementsTransaction?._id,
          },
        })
      );
    }
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          fileUploads,
        }}
        onSubmit={() => null}
      >
        {() => {
          return (
            <div className="p-4">
              {yyyyFinancialStatementsComments.length > 0 && (
                <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                  <CommentsList
                    comments={yyyyFinancialStatementsComments}
                    toggleCommentStatus={true}
                  />
                  <p className="mt-6 text-sm text-primary">
                    Comments: {yyyyFinancialStatementsComments.length} of{" "}
                    {yyyyFinancialStatementsComments.length}
                  </p>
                </div>
              )}
              <div>
                {documentData.map((document, index) => (
                  <div
                    key={index}
                    className="shadow-sm sm:rounded-md sm:overflow-hidden px-4 py-6 space-y-4 bg-gray-50 sm:p-6 mt-4"
                  >
                    <div className=" mb-4">
                      <h2 className="text-4xl font-bold text-gray-800">
                        {new Date(document.yearOfStatement).getFullYear()}
                      </h2>
                      <p className="text-gray-800 font-bold">
                        Financial Statements
                      </p>
                    </div>
                    <div className="bg-white p-2">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={[documentData[index]]}
                        resolvedFields={_get(
                          yyyyFinancialStatementsTransaction,
                          "resolvedFields"
                        )}
                        onlyPreview
                        showDocumentLabel
                        addReviewComment={!isViewMode}
                        hideResolveAllCheckbox={isViewMode}
                        handleSaveComment={handleSaveComment}
                        formName="financialStatements"
                        formHelperKey={formHelperKey}
                        updateEffectKey={updateEffectKey}
                        updateEffectPath={
                          yyyyFinancialStatementsUpdateEffectPath
                        }
                        formId={_get(yyyyFinancialStatementsTransaction, "_id")}
                        resolveAllFieldsCheckbox
                        fileUploadComments={yyyyFinancialStatementsComments}
                      />
                    </div>
                    <FormFieldsCard
                      fields={[
                        {
                          key: "Auditor Approval Date",
                          value: format(
                            new Date(document.auditorApprovalDate),
                            "yyyy/MM/dd"
                          ),
                          name: "auditorApprovalDate",
                        },
                        {
                          key: "Board Approval Date",
                          value: format(
                            new Date(document.boardApprovalDate),
                            "yyyy/MM/dd"
                          ),
                          name: "boardApprovalDate",
                        },
                      ]}
                      resolvedFields={_get(
                        yyyyFinancialStatementsTransaction,
                        "resolvedFields"
                      )}
                      onlyPreview
                      showDocumentLabel
                      addReviewComment={!isViewMode}
                      hideResolveAllCheckbox={isViewMode}
                      handleSaveComment={handleSaveComment}
                      formName="financialStatements"
                      formHelperKey={formHelperKey}
                      updateEffectKey={updateEffectKey}
                      updateEffectPath={yyyyFinancialStatementsUpdateEffectPath}
                      formId={_get(yyyyFinancialStatementsTransaction, "_id")}
                      resolveAllFieldsCheckbox
                      comments={yyyyFinancialStatementsComments}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default FinancialStatements;

function buildUpdateEffectPath(sliceIndex) {
  return `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`;
}

function generateFileUploadsFromFiancialStatementsArray(
  financialStatementsArray
) {
  const fileUploads = {};
  financialStatementsArray.forEach((returnObj, index) => {
    fileUploads[`financialStatement${index + 1}`] =
      returnObj.financialStatements.fileName;
  });
  return fileUploads;
}

function generateDocumentDataFromFinancialStatementsArray(
  financialStatementsArray
) {
  const documentData = [];
  financialStatementsArray.forEach((returnObj, index) => {
    documentData.push({
      label: `Financial Statements`,
      name: `financialStatement${index}`,
      accept: "application/pdf",
      ...returnObj,
    });
  });
  return documentData;
}
